import React, { ChangeEvent, ReactNode, useEffect, useState } from "react";
import classNames from "classnames";

import FormElementErrorMessage from "@/components/botanic/FormElementErrorMessage";
import FormElementLabel from "@/components/botanic/FormElementLabel";
import FormField from "@/components/FormField";

import styles from "./Select.module.css";

type SelectProps = {
  activeOnLoad?: boolean;
  children: ReactNode;
  className?: string;
  selectClassName?: string;
  dynamicLabel?: boolean;
  errorMessage?: string;
  helperText?: string;
  id: string;
  label?: string;
  onBlur?: () => void;
  onChange?: (e: ChangeEvent<HTMLSelectElement>) => void;
} & React.SelectHTMLAttributes<HTMLSelectElement>;

const Select: React.FC<SelectProps> = ({
  activeOnLoad = true,
  children,
  className,
  selectClassName,
  dynamicLabel = false,
  errorMessage,
  helperText,
  id,
  label,
  onBlur = () => {},
  onChange = () => {},
  ...others
}) => {
  const selectRef = React.useRef<HTMLSelectElement>(null);

  const [isActive, _setIsActive] = useState(!dynamicLabel || activeOnLoad);
  const setIsActive = (bool: boolean) => dynamicLabel && _setIsActive(bool);

  useEffect(() => {
    setIsActive(!!selectRef.current?.value);
  }, []);

  const classes = classNames(
    {
      [styles["form__field"]]: !isActive,
      "form__field--active": isActive,
    },
    className,
  );

  return (
    <FormField className={classes}>
      {label ? (
        <FormElementLabel
          className={styles["botanic-select-label"]}
          hasError={!!errorMessage}
          htmlFor={id}
          label={label}
        />
      ) : (
        <></>
      )}
      <select
        id={id}
        ref={selectRef}
        className={classNames(
          styles["botanic-select"],
          "[&_option]:pl-0",
          { unlabeled: !label },
          selectClassName,
        )}
        onChange={(e) => {
          onChange(e);
          setIsActive(!!selectRef.current?.value);
        }}
        onBlur={(e) => {
          onBlur(e);
          setIsActive(!!selectRef.current?.value);
        }}
        {...others}
      >
        {children}
      </select>
      {helperText && !errorMessage ? <p>{helperText}</p> : <></>}
      {errorMessage ? (
        <FormElementErrorMessage message={errorMessage} />
      ) : (
        <></>
      )}
    </FormField>
  );
};

export default Select;
